import { StandardMemberAttributesDto } from '@act/shared/data-transfer-objects';
import { CollapsibleCard, IconLibrary } from '@act/storybook';
import { Typography } from '@mui/material';
import { Fragment } from 'react/jsx-runtime';
import { LabeledDatum } from 'toolkit/styles/dataDisplay';
import ProviderEntry from './ProviderEntry';
import { getClientFeedDataForSection } from './getClientFeedData';
import { areAttributesEmpty } from './areAttributesEmpty';

export interface ProviderInformationCardProps {
  attributes: Pick<
    StandardMemberAttributesDto,
    | 'careNavigator'
    | 'caseManager'
    | 'hospitals'
    | 'pcpName'
    | 'pcpAddressLine1'
    | 'pcpAddressLine2'
    | 'pcpAddressCity'
    | 'pcpAddressState'
    | 'pcpAddressZip'
    | 'pcpPhoneNumber'
    | 'clientFeedData'
  >;
  defaultClosed?: boolean;
  onToggle?: () => void;
}

const ProviderInformationCard = ({
  attributes,
  defaultClosed = false,
  onToggle,
}: ProviderInformationCardProps) => {
  const pcpDetailsAreEmpty = areAttributesEmpty(attributes, [
    'pcpName',
    'pcpAddressLine1',
    'pcpAddressLine2',
    'pcpAddressCity',
    'pcpAddressState',
    'pcpAddressZip',
    'pcpPhoneNumber',
  ]);

  const careDetailsAreEmpty = areAttributesEmpty(attributes, [
    'careNavigator',
    'caseManager',
    'hospitals',
  ]);

  const clientFeedData = getClientFeedDataForSection(
    attributes?.clientFeedData,
    'provider'
  );

  return (
    <CollapsibleCard
      title="Provider information"
      avatar={<IconLibrary iconName="Asclepius" size={24} />}
      twoColumn
      defaultClosed={defaultClosed}
      onToggle={onToggle}
    >
      {pcpDetailsAreEmpty && careDetailsAreEmpty && !clientFeedData?.length && (
        <Typography variant="bodyMedium">
          No provider information to show.
        </Typography>
      )}
      {!pcpDetailsAreEmpty && (
        <ProviderEntry
          name={attributes.pcpName}
          addressLine1={attributes.pcpAddressLine1}
          addressLine2={attributes.pcpAddressLine2}
          city={attributes.pcpAddressCity}
          state={attributes.pcpAddressState}
          zip={attributes.pcpAddressZip}
          phoneNumber={attributes.pcpPhoneNumber}
        />
      )}
      {attributes?.hospitals && (
        <LabeledDatum label="Hospital" datum={attributes.hospitals} />
      )}
      {attributes?.careNavigator && (
        <LabeledDatum label="Care navigator" datum={attributes.careNavigator} />
      )}
      {attributes?.caseManager && (
        <LabeledDatum label="Case manager" datum={attributes.caseManager} />
      )}
      {clientFeedData?.length > 0 && (
        <Fragment>
          {clientFeedData.map(data => {
            return (
              <LabeledDatum
                key={data.field_label}
                label={data.field_label}
                datum={data.field_value}
              />
            );
          })}
        </Fragment>
      )}
    </CollapsibleCard>
  );
};

export default ProviderInformationCard;
